import type { ColoringTemplate, LivingScene } from '@/contracts/content';

/**
 * Coloring templates.
 *
 * Three deliberately different aspect ratios, because the canvas must honour
 * each template's own document size rather than squashing everything into one
 * fixed square (spec §9.1).
 *
 * `regions` are closed paths — they ARE the fill masks, so "Fill" is an exact
 * O(1) region assignment instead of a flood-fill that leaks through
 * antialiased line art. `outline` is stroked on top and is never erasable.
 */

export const TEMPLATE_PIP_POND: ColoringTemplate = {
  id: 'tpl_pip_pond',
  revision: 1,
  title: 'Pip at the Pond',
  width: 640,
  height: 480,
  livingSceneId: null,
  worldSlots: ['wall-frame', 'shelf'],
  regions: [
    { id: 'sky', label: 'The night sky', hint: '#3A2A63', d: 'M0 0 H640 V300 H0 Z' },
    { id: 'moon', label: 'The moon', hint: '#FFF0BE', d: 'M520 96 m-52 0 a52 52 0 1 0 104 0 a52 52 0 1 0 -104 0 Z' },
    { id: 'water', label: 'The pond', hint: '#4E96A0', d: 'M0 300 C120 286 240 306 380 298 C480 292 560 302 640 296 V480 H0 Z' },
    { id: 'bank', label: 'The grassy bank', hint: '#4E8F63', d: 'M0 300 C90 288 150 292 214 300 C168 322 74 330 0 326 Z' },
    { id: 'fox-tail', label: "Pip's tail", hint: '#E8823C', d: 'M150 372 C86 366 62 312 98 276 C120 254 150 262 156 292 C164 322 150 340 150 372 Z' },
    { id: 'fox-tail-tip', label: 'Tail tip', hint: '#FFF1DC', d: 'M96 280 C112 260 138 262 148 286 C130 278 108 286 96 280 Z' },
    { id: 'fox-body', label: "Pip's body", hint: '#E8823C', d: 'M148 378 C148 320 190 286 242 286 C294 286 328 320 328 378 C328 402 306 414 242 414 C178 414 148 402 148 378 Z' },
    { id: 'fox-chest', label: "Pip's chest", hint: '#FFF1DC', d: 'M200 382 C200 348 220 328 242 328 C264 328 284 348 284 382 C284 400 266 406 242 406 C218 406 200 400 200 382 Z' },
    { id: 'fox-head', label: "Pip's face", hint: '#E8823C', d: 'M286 268 m-58 0 a58 52 0 1 0 116 0 a58 52 0 1 0 -116 0 Z' },
    { id: 'fox-snout', label: "Pip's snout", hint: '#FFF1DC', d: 'M262 286 C262 266 278 254 294 254 C316 254 334 272 334 292 C334 308 312 318 294 318 C276 318 262 304 262 286 Z' },
    { id: 'fox-ear-l', label: 'Left ear', hint: '#CE6524', d: 'M244 226 L220 176 L272 202 Z' },
    { id: 'fox-ear-r', label: 'Right ear', hint: '#CE6524', d: 'M328 226 L352 176 L300 202 Z' },
    { id: 'reed-a', label: 'Reeds', hint: '#3F7F63', d: 'M568 310 C556 264 566 226 588 196 L602 202 C584 232 576 268 584 310 Z' },
    { id: 'reed-b', label: 'More reeds', hint: '#4C9873', d: 'M604 312 C598 274 610 240 628 216 L640 224 C624 246 614 276 618 312 Z' },
    { id: 'reflection', label: 'Moon on the water', hint: '#FFF0BE', d: 'M520 372 m-40 0 a40 16 0 1 0 80 0 a40 16 0 1 0 -80 0 Z' },
  ],
  outline: `
    M468 96 a52 52 0 1 0 104 0 a52 52 0 1 0 -104 0
    M150 372 C86 366 62 312 98 276 C120 254 150 262 156 292 C164 322 150 340 150 372 Z
    M96 280 C112 260 138 262 148 286
    M148 378 C148 320 190 286 242 286 C294 286 328 320 328 378 C328 402 306 414 242 414 C178 414 148 402 148 378 Z
    M200 382 C200 348 220 328 242 328 C264 328 284 348 284 382 C284 400 266 406 242 406 C218 406 200 400 200 382 Z
    M228 268 a58 52 0 1 0 116 0 a58 52 0 1 0 -116 0
    M262 286 C262 266 278 254 294 254 C316 254 334 272 334 292 C334 308 312 318 294 318 C276 318 262 304 262 286 Z
    M244 226 L220 176 L272 202 Z
    M328 226 L352 176 L300 202 Z
    M568 310 C556 264 566 226 588 196 L602 202 C584 232 576 268 584 310 Z
    M604 312 C598 274 610 240 628 216 L640 224 C624 246 614 276 618 312 Z
    M480 372 a40 16 0 1 0 80 0 a40 16 0 1 0 -80 0
  `,
  backgroundOutline: `
    M0 300 C120 286 240 306 380 298 C480 292 560 302 640 296
    M0 300 C90 288 150 292 214 300 C168 322 74 330 0 326
    M120 396 q30 -8 60 0 M360 420 q34 -9 68 0
  `,
  detail: `
    M270 262 a8 9 0 1 0 0.1 0 Z
    M312 262 a8 9 0 1 0 0.1 0 Z
    M320 296 a10 8 0 1 0 0.1 0 Z
  `,
};

export const TEMPLATE_SWAY: ColoringTemplate = {
  id: 'tpl_sway_wings',
  revision: 2,
  title: "Sway's Lantern Wings",
  width: 560,
  height: 560,
  livingSceneId: 'scene_sway_flight',
  worldSlots: ['window', 'wall-frame', 'garden'],
  regions: [
    // Listed first, so it is hit-tested LAST: a tap anywhere that is not part
    // of the moth falls through to the background.
    { id: 'sky', label: 'The night behind', hint: '#2E2356', d: 'M0 0 H560 V560 H0 Z' },
    { id: 'wing-upper-l', label: 'Upper left wing', hint: '#5FC9D6', d: 'M270 244 C232 152 132 108 80 158 C36 202 60 288 152 326 C192 342 242 326 270 310 Z' },
    { id: 'wing-upper-r', label: 'Upper right wing', hint: '#5FC9D6', d: 'M290 244 C328 152 428 108 480 158 C524 202 500 288 408 326 C368 342 318 326 290 310 Z' },
    { id: 'wing-lower-l', label: 'Lower left wing', hint: '#8E6FE0', d: 'M268 322 C216 334 158 366 158 414 C158 456 210 476 248 444 C268 428 270 378 268 322 Z' },
    { id: 'wing-lower-r', label: 'Lower right wing', hint: '#8E6FE0', d: 'M292 322 C344 334 402 366 402 414 C402 456 350 476 312 444 C292 428 290 378 292 322 Z' },
    { id: 'spot-l', label: 'Left wing spot', hint: '#FFD98A', d: 'M162 222 m-30 0 a30 30 0 1 0 60 0 a30 30 0 1 0 -60 0 Z' },
    { id: 'spot-r', label: 'Right wing spot', hint: '#FFD98A', d: 'M398 222 m-30 0 a30 30 0 1 0 60 0 a30 30 0 1 0 -60 0 Z' },
    { id: 'moon-l', label: 'Left wing moon', hint: '#FFF6D8', d: 'M214 400 m-20 0 a20 20 0 1 0 40 0 a20 20 0 1 0 -40 0 Z' },
    { id: 'moon-r', label: 'Right wing moon', hint: '#FFF6D8', d: 'M346 400 m-20 0 a20 20 0 1 0 40 0 a20 20 0 1 0 -40 0 Z' },
    { id: 'body', label: "Sway's body", hint: '#3C2A50', d: 'M280 212 C298 212 304 244 302 304 C300 364 292 416 280 416 C268 416 260 364 258 304 C256 244 262 212 280 212 Z' },
    { id: 'head', label: "Sway's head", hint: '#3C2A50', d: 'M280 196 m-27 0 a27 25 0 1 0 54 0 a27 25 0 1 0 -54 0 Z' },
  ],
  outline: `
    M270 244 C232 152 132 108 80 158 C36 202 60 288 152 326 C192 342 242 326 270 310 Z
    M290 244 C328 152 428 108 480 158 C524 202 500 288 408 326 C368 342 318 326 290 310 Z
    M268 322 C216 334 158 366 158 414 C158 456 210 476 248 444 C268 428 270 378 268 322 Z
    M292 322 C344 334 402 366 402 414 C402 456 350 476 312 444 C292 428 290 378 292 322 Z
    M132 222 a30 30 0 1 0 60 0 a30 30 0 1 0 -60 0
    M368 222 a30 30 0 1 0 60 0 a30 30 0 1 0 -60 0
    M194 400 a20 20 0 1 0 40 0 a20 20 0 1 0 -40 0
    M326 400 a20 20 0 1 0 40 0 a20 20 0 1 0 -40 0
    M280 212 C298 212 304 244 302 304 C300 364 292 416 280 416 C268 416 260 364 258 304 C256 244 262 212 280 212 Z
    M253 196 a27 25 0 1 0 54 0 a27 25 0 1 0 -54 0
    M268 174 C256 142 234 128 214 132
    M292 174 C304 142 326 128 346 132
    M259 268 h42 M261 320 h38 M264 368 h32
    M120 200 C150 214 186 226 236 236
    M440 200 C410 214 374 226 324 236
  `,
  detail: `
    M269 190 a6 6 0 1 0 0.1 0 Z
    M291 190 a6 6 0 1 0 0.1 0 Z
  `,
};

export const TEMPLATE_PAPER_MOON: ColoringTemplate = {
  id: 'tpl_paper_moon',
  revision: 1,
  title: 'The Paper Moon',
  width: 480,
  height: 640,
  livingSceneId: 'scene_moon_rise',
  worldSlots: ['window', 'wall-frame'],
  regions: [
    { id: 'night', label: 'Night behind it', hint: '#241B44', d: 'M0 0 H480 V640 H0 Z' },
    { id: 'glow', label: 'The halo', hint: '#FFD98A', d: 'M240 232 m-150 0 a150 150 0 1 0 300 0 a150 150 0 1 0 -300 0 Z' },
    { id: 'moon-a', label: 'Moon — left half', hint: '#FFF6D8', d: 'M240 108 L240 332 L172 372 L192 292 L124 240 L206 230 Z' },
    { id: 'moon-b', label: 'Moon — right half', hint: '#FFE9A8', d: 'M240 108 L274 230 L356 240 L288 292 L308 372 L240 332 Z' },
    { id: 'string', label: 'The string', hint: '#D9C9A0', d: 'M236 372 h8 v128 h-8 Z' },
    { id: 'spool', label: "Pip's paper spool", hint: '#E8823C', d: 'M180 500 C180 480 204 468 240 468 C276 468 300 480 300 500 L300 560 C300 580 276 592 240 592 C204 592 180 580 180 560 Z' },
    { id: 'spool-band', label: 'The label', hint: '#FFF1DC', d: 'M180 516 L300 516 L300 548 L180 548 Z' },
    { id: 'star-a', label: 'A little star', hint: '#FFF4D6', d: 'M74 92 L82 116 L106 124 L82 132 L74 156 L66 132 L42 124 L66 116 Z' },
    { id: 'star-b', label: 'Another star', hint: '#FFF4D6', d: 'M404 428 L410 446 L428 452 L410 458 L404 476 L398 458 L380 452 L398 446 Z' },
  ],
  outline: `
    M90 232 a150 150 0 1 0 300 0 a150 150 0 1 0 -300 0
    M240 108 L274 230 L356 240 L288 292 L308 372 L240 332 L172 372 L192 292 L124 240 L206 230 Z
    M240 108 L240 332
    M236 372 h8 v128 h-8 Z
    M180 500 C180 480 204 468 240 468 C276 468 300 480 300 500 L300 560 C300 580 276 592 240 592 C204 592 180 580 180 560 Z
    M180 516 L300 516 M180 548 L300 548
    M74 92 L82 116 L106 124 L82 132 L74 156 L66 132 L42 124 L66 116 Z
    M404 428 L410 446 L428 452 L410 458 L404 476 L398 458 L380 452 L398 446 Z
  `,
};


/* ------------------------------------------------------------------ *
 * Story 2 — Pip and the Loudest Night
 *
 * Authored against the same contract as story 1 with no new engine code.
 * Shapes differ again (wide, square, portrait) so the canvas fit is
 * exercised rather than assumed.
 * ------------------------------------------------------------------ */

export const TEMPLATE_LEAF_DEN: ColoringTemplate = {
  id: 'tpl_leaf_den',
  revision: 1,
  title: 'The Leaf Den',
  width: 720,
  height: 460,
  livingSceneId: null,
  worldSlots: ['wall-frame', 'shelf'],
  regions: [
    { id: 'sky', label: 'The rainy sky', hint: '#33285A', d: 'M0 0 H720 V300 H0 Z' },
    { id: 'ground', label: 'The ground', hint: '#5C4130', d: 'M0 300 H720 V460 H0 Z' },
    { id: 'den-outer', label: 'The outside of the den', hint: '#3F7B54', d: 'M96 356 C96 208 260 140 360 140 C460 140 624 208 624 356 Z' },
    { id: 'den-inner', label: 'Inside the den', hint: '#2C5C3E', d: 'M164 356 C164 250 288 194 360 194 C432 194 556 250 556 356 Z' },
    { id: 'leaf-a', label: 'A big leaf', hint: '#4E8F63', d: 'M150 214 m-52 -24 a52 24 0 1 0 104 0 a52 24 0 1 0 -104 0 Z' },
    { id: 'leaf-b', label: 'Another leaf', hint: '#5CA372', d: 'M360 156 m-56 -22 a56 22 0 1 0 112 0 a56 22 0 1 0 -112 0 Z' },
    { id: 'leaf-c', label: 'One more leaf', hint: '#4E8F63', d: 'M572 214 m-52 -24 a52 24 0 1 0 104 0 a52 24 0 1 0 -104 0 Z' },
    { id: 'fox', label: 'Pip, curled up', hint: '#E8823C', d: 'M268 356 C268 300 306 268 356 268 C406 268 442 300 442 356 Z' },
    { id: 'fox-chest', label: "Pip's chest", hint: '#FFF1DC', d: 'M320 356 C320 322 336 306 356 306 C376 306 392 322 392 356 Z' },
    { id: 'moth', label: 'Sway', hint: '#5FC9D6', d: 'M498 264 m-30 0 a30 20 0 1 0 60 0 a30 20 0 1 0 -60 0 Z' },
  ],
  outline: `
    M96 356 C96 208 260 140 360 140 C460 140 624 208 624 356 Z
    M164 356 C164 250 288 194 360 194 C432 194 556 250 556 356 Z
    M98 190 a52 24 0 1 0 104 0 a52 24 0 1 0 -104 0
    M304 134 a56 22 0 1 0 112 0 a56 22 0 1 0 -112 0
    M520 190 a52 24 0 1 0 104 0 a52 24 0 1 0 -104 0
    M268 356 C268 300 306 268 356 268 C406 268 442 300 442 356 Z
    M320 356 C320 322 336 306 356 306 C376 306 392 322 392 356 Z
    M468 264 a30 20 0 1 0 60 0 a30 20 0 1 0 -60 0
    M498 244 v-16 M486 232 l-10 -10 M510 232 l10 -10
  `,
  backgroundOutline: `
    M0 300 H720
    M60 392 q40 -12 80 0 M580 404 q44 -12 88 0
  `,
  detail: `
    M340 320 a7 8 0 1 0 0.1 0 Z
    M376 320 a7 8 0 1 0 0.1 0 Z
  `,
};

export const TEMPLATE_RAIN_POND: ColoringTemplate = {
  id: 'tpl_rain_pond',
  revision: 1,
  title: 'Rain on the Pond',
  width: 600,
  height: 600,
  livingSceneId: null,
  worldSlots: ['wall-frame', 'window'],
  regions: [
    { id: 'water', label: 'The water', hint: '#4E96A0', d: 'M0 0 H600 V600 H0 Z' },
    { id: 'ring-1', label: 'The biggest ripple', hint: '#7FC4CC', d: 'M300 320 m-240 0 a240 240 0 1 0 480 0 a240 240 0 1 0 -480 0 Z' },
    { id: 'ring-2', label: 'The next ripple', hint: '#8FD3E8', d: 'M300 320 m-176 0 a176 176 0 1 0 352 0 a176 176 0 1 0 -352 0 Z' },
    { id: 'ring-3', label: 'A smaller ripple', hint: '#BFE9F6', d: 'M300 320 m-112 0 a112 112 0 1 0 224 0 a112 112 0 1 0 -224 0 Z' },
    { id: 'ring-4', label: 'The middle', hint: '#DCF1F7', d: 'M300 320 m-52 0 a52 52 0 1 0 104 0 a52 52 0 1 0 -104 0 Z' },
    { id: 'lily-a', label: 'A lily pad', hint: '#4E8F63', d: 'M126 468 m-54 0 a54 30 0 1 0 108 0 a54 30 0 1 0 -108 0 Z' },
    { id: 'lily-b', label: 'Another lily pad', hint: '#5CA372', d: 'M476 150 m-46 0 a46 26 0 1 0 92 0 a46 26 0 1 0 -92 0 Z' },
    { id: 'drop-a', label: 'A falling drop', hint: '#FFFFFF', d: 'M150 100 C162 122 168 134 168 144 a18 18 0 1 1 -36 0 c0 -10 6 -22 18 -44 Z' },
    { id: 'drop-b', label: 'Another drop', hint: '#FFFFFF', d: 'M452 402 C462 420 467 430 467 438 a15 15 0 1 1 -30 0 c0 -8 5 -18 15 -36 Z' },
  ],
  outline: `
    M60 320 a240 240 0 1 0 480 0 a240 240 0 1 0 -480 0
    M124 320 a176 176 0 1 0 352 0 a176 176 0 1 0 -352 0
    M188 320 a112 112 0 1 0 224 0 a112 112 0 1 0 -224 0
    M248 320 a52 52 0 1 0 104 0 a52 52 0 1 0 -104 0
    M72 468 a54 30 0 1 0 108 0 a54 30 0 1 0 -108 0
    M430 150 a46 26 0 1 0 92 0 a46 26 0 1 0 -92 0
    M150 100 C162 122 168 134 168 144 a18 18 0 1 1 -36 0 c0 -10 6 -22 18 -44 Z
    M452 402 C462 420 467 430 467 438 a15 15 0 1 1 -30 0 c0 -8 5 -18 15 -36 Z
    M126 452 l30 16 M476 140 l24 12
  `,
};

export const TEMPLATE_MORNING_PUDDLE: ColoringTemplate = {
  id: 'tpl_morning_puddle',
  revision: 1,
  title: 'A Puddle Full of Sky',
  width: 460,
  height: 620,
  livingSceneId: 'scene_puddle_sky',
  worldSlots: ['window', 'garden', 'wall-frame'],
  regions: [
    { id: 'above', label: 'The morning sky', hint: '#BFE3EE', d: 'M0 0 H460 V300 H0 Z' },
    { id: 'grass', label: 'The wet grass', hint: '#6FA85F', d: 'M0 300 H460 V620 H0 Z' },
    { id: 'sun', label: 'The sun', hint: '#FFD98A', d: 'M340 96 m-52 0 a52 52 0 1 0 104 0 a52 52 0 1 0 -104 0 Z' },
    { id: 'cloud', label: 'A cloud', hint: '#FFFFFF', d: 'M120 120 C120 92 148 76 172 86 C186 60 232 62 240 96 C266 96 274 126 252 138 L136 138 C118 136 114 126 120 120 Z' },
    { id: 'puddle', label: 'The puddle', hint: '#8FD3E8', d: 'M230 440 m-186 0 a186 106 0 1 0 372 0 a186 106 0 1 0 -372 0 Z' },
    { id: 'puddle-sky', label: 'The sky inside the puddle', hint: '#DCF1F7', d: 'M230 436 m-150 0 a150 82 0 1 0 300 0 a150 82 0 1 0 -300 0 Z' },
    { id: 'sun-reflection', label: 'The sun in the puddle', hint: '#FFE9A8', d: 'M300 412 m-38 0 a38 20 0 1 0 76 0 a38 20 0 1 0 -76 0 Z' },
    { id: 'stone', label: 'A stone', hint: '#9AA3AC', d: 'M92 570 C92 548 116 536 140 540 C166 544 178 560 172 578 C166 594 108 594 92 570 Z' },
  ],
  outline: `
    M288 96 a52 52 0 1 0 104 0 a52 52 0 1 0 -104 0
    M120 120 C120 92 148 76 172 86 C186 60 232 62 240 96 C266 96 274 126 252 138 L136 138 C118 136 114 126 120 120 Z
    M44 440 a186 106 0 1 0 372 0 a186 106 0 1 0 -372 0
    M80 436 a150 82 0 1 0 300 0 a150 82 0 1 0 -300 0
    M262 412 a38 20 0 1 0 76 0 a38 20 0 1 0 -76 0
    M92 570 C92 548 116 536 140 540 C166 544 178 560 172 578 C166 594 108 594 92 570 Z
  `,
  backgroundOutline: `
    M0 300 H460
    M120 332 q26 -22 52 0 M300 336 q24 -20 48 0 M60 350 q22 -18 44 0
  `,
};

export const TEMPLATES = [
  TEMPLATE_PIP_POND,
  TEMPLATE_SWAY,
  TEMPLATE_PAPER_MOON,
  TEMPLATE_LEAF_DEN,
  TEMPLATE_RAIN_POND,
  TEMPLATE_MORNING_PUDDLE,
];

/**
 * Living scenes (spec §10.2).
 *
 * Deterministic, authored, offline-capable. Each step transforms the child's
 * OWN artwork — nothing regenerates or reinterprets the drawing.
 * A template only advertises "Bring to life" when a scene exists for its
 * exact revision (spec §10.2).
 */
/**
 * Nib the snail.
 *
 * The shell is deliberately the largest shape and is split into three bands
 * rather than left as one disc: a spiral a child can colour in sections is a
 * far better picture than a spiral they can only flood with one colour, and
 * the bands follow the drawn spiral so the result still reads as a shell.
 */
export const TEMPLATE_NIB_SHELL: ColoringTemplate = {
  id: 'tpl_nib_shell',
  revision: 1,
  title: 'Nib and His Shell',
  width: 660,
  height: 440,
  livingSceneId: 'scene_nib_glide',
  worldSlots: ['shelf', 'wall-frame', 'garden'],
  /**
   * Every path here is Nib's own geometry from `scenes.tsx`, scaled six times
   * and moved onto this canvas. That is deliberate: a colouring page of a
   * character has to be recognisably THAT character, and a redrawn-by-hand
   * version drifts into being a different animal that happens to be a snail.
   */
  regions: [
    // Listed first so it is hit-tested last: a tap that misses Nib and misses
    // the leaf lands on the morning behind them.
    { id: 'morning', label: 'The morning behind', hint: '#DCF1F7', d: 'M0 0 H660 V440 H0 Z' },
    { id: 'leaf', label: 'The leaf he is on', hint: '#5EA469', d: 'M30 398 C70 350 220 328 380 338 C500 346 604 366 652 392 C594 414 468 430 308 430 C158 430 14 420 30 398 Z' },
    { id: 'foot', label: 'His long foot', hint: '#EFDFCB', d: 'M 98 340 C 98 314 150 300 240 300 C 330 300 424 308 470 322 C 506 334 506 356 460 360 L 128 360 C 104 360 98 354 98 340 Z' },
    // Three bands rather than one disc: a spiral a child can colour in
    // sections is a better picture than one they can only flood.
    { id: 'shell-outer', label: 'The outside of the shell', hint: '#D8A05C', d: 'M246 196 m-114 0 a114 114 0 1 0 228 0 a114 114 0 1 0 -228 0 Z' },
    { id: 'shell-middle', label: 'The middle band', hint: '#E9BE7E', d: 'M246 196 m-75 0 a75 75 0 1 0 150 0 a75 75 0 1 0 -150 0 Z' },
    { id: 'shell-inner', label: 'The middle of the spiral', hint: '#F3DCB8', d: 'M246 196 m-36 0 a36 36 0 1 0 72 0 a36 36 0 1 0 -72 0 Z' },
    { id: 'head', label: 'His head', hint: '#EFDFCB', d: 'M468 238 m-60 0 a60 54 0 1 0 120 0 a60 54 0 1 0 -120 0 Z' },
    { id: 'stalk-l', label: 'Left eye stalk', hint: '#EFDFCB', d: 'M 438 208 C 426 160 402 124 378 94 C 366 82 342 88 348 106 C 360 142 390 184 414 220 Z' },
    { id: 'stalk-r', label: 'Right eye stalk', hint: '#EFDFCB', d: 'M 486 208 C 510 166 540 136 570 118 C 582 112 600 130 588 142 C 558 172 528 208 510 232 Z' },
  ],
  outline: `
    M 98 346 C 98 328 112 314 140 305
    M 360 305 C 404 310 446 316 470 322 C 506 334 506 356 460 360 L 130 360 C 102 360 98 356 98 346
    M132 196 a114 114 0 1 0 228 0 a114 114 0 1 0 -228 0
    M246 82 A114 114 0 1 1 168 274 A84 84 0 1 0 264 166 A48 48 0 1 1 246 256
    M408 238 a60 54 0 1 0 120 0 a60 54 0 1 0 -120 0
    M 438 208 C 426 160 402 124 378 94 C 366 82 342 88 348 106 C 360 142 390 184 414 220 Z
    M 486 208 C 510 166 540 136 570 118 C 582 112 600 130 588 142 C 558 172 528 208 510 232 Z
    M 438 262 C 456 274 480 274 498 262
    M 136 338 C 226 324 356 322 452 338
  `,
  /* The leaf is background: a cutout of Nib alone must not trail it. */
  backgroundOutline: `
    M30 398 C70 350 220 328 380 338 C500 346 604 366 652 392 C594 414 468 430 308 430 C158 430 14 420 30 398 Z
    M58 402 C220 378 460 376 646 392
    M140 396 C160 382 176 372 200 360
    M240 402 C260 386 278 376 306 362
    M344 406 C366 390 386 380 416 368
    M450 408 C472 394 492 386 522 376
  `,
  detail: `
    M372 82 m-16 0 a16 16 0 1 0 32 0 a16 16 0 1 0 -32 0 Z
    M576 112 m-16 0 a16 16 0 1 0 32 0 a16 16 0 1 0 -32 0 Z
  `,
};

export const TEMPLATE_DEW_LEAF: ColoringTemplate = {
  id: 'tpl_dew_leaf',
  revision: 1,
  title: 'The Leaf That Held the Morning',
  width: 620,
  height: 460,
  livingSceneId: null,
  worldSlots: ['shelf', 'window', 'garden'],
  regions: [
    { id: 'air', label: 'The air around it', hint: '#EAF6FA', d: 'M0 0 H620 V460 H0 Z' },
    { id: 'leaf-body', label: 'The leaf', hint: '#5EA469', d: 'M40 268 C66 154 200 74 348 92 C480 108 570 186 554 272 C538 356 372 406 226 386 C110 370 26 336 40 268 Z' },
    { id: 'drop-big', label: 'The biggest drop', hint: '#BFE8F2', d: 'M262 196 m-58 0 a58 58 0 1 0 116 0 a58 58 0 1 0 -116 0 Z' },
    { id: 'drop-mid', label: 'The middle drop', hint: '#BFE8F2', d: 'M404 254 m-40 0 a40 40 0 1 0 80 0 a40 40 0 1 0 -80 0 Z' },
    { id: 'drop-small', label: 'The smallest drop', hint: '#BFE8F2', d: 'M144 288 m-26 0 a26 26 0 1 0 52 0 a26 26 0 1 0 -52 0 Z' },
    { id: 'boat', label: 'The seed boat', hint: '#C99A5E', d: 'M444 366 C452 396 546 396 554 366 C532 378 466 378 444 366 Z' },
    { id: 'sail', label: 'Its little sail', hint: '#FFF3D8', d: 'M498 364 L498 300 L544 332 Z' },
  ],
  outline: `
    M204 196 a58 58 0 1 0 116 0 a58 58 0 1 0 -116 0
    M364 254 a40 40 0 1 0 80 0 a40 40 0 1 0 -80 0
    M118 288 a26 26 0 1 0 52 0 a26 26 0 1 0 -52 0
    M444 366 C452 396 546 396 554 366 C532 378 466 378 444 366 Z
    M498 364 L498 300 L544 332 Z
    M498 300 L498 366
    M232 168 A44 44 0 0 1 276 150
    M386 232 A30 30 0 0 1 412 220
  `,
  backgroundOutline: `
    M40 268 C66 154 200 74 348 92 C480 108 570 186 554 272 C538 356 372 406 226 386 C110 370 26 336 40 268 Z
    M56 276 C180 240 380 226 548 264
    M150 356 C176 316 196 288 226 250
    M258 372 C284 326 306 300 338 256
    M366 376 C384 340 404 312 436 280
  `,
};

/**
 * The morning garden itself — a wide picture with big, calm areas, for a
 * child who wants to fill rather than to detail.
 */
export const TEMPLATE_GARDEN_MORNING: ColoringTemplate = {
  id: 'tpl_garden_morning',
  revision: 1,
  title: 'The Garden, Slowly',
  width: 660,
  height: 440,
  livingSceneId: null,
  worldSlots: ['wall-frame', 'shelf'],
  regions: [
    { id: 'sky', label: 'The sky', hint: '#CFEAF5', d: 'M0 0 H660 V240 H0 Z' },
    { id: 'sun', label: 'The sun', hint: '#FFE9A8', d: 'M548 86 m-54 0 a54 54 0 1 0 108 0 a54 54 0 1 0 -108 0 Z' },
    { id: 'far-grass', label: 'The far grass', hint: '#8CC08E', d: 'M0 232 C90 206 176 232 258 216 C346 198 408 226 490 210 C560 196 616 220 660 208 L660 300 L0 300 Z' },
    { id: 'near-grass', label: 'The near grass', hint: '#5EA469', d: 'M0 296 C110 276 210 300 320 288 C430 276 540 300 660 286 L660 440 L0 440 Z' },
    { id: 'flower-a', label: 'The pink flower', hint: '#F2A8C8', d: 'M128 320 m-34 0 a34 34 0 1 0 68 0 a34 34 0 1 0 -68 0 Z' },
    { id: 'flower-b', label: 'The yellow flower', hint: '#FFD98A', d: 'M556 336 m-30 0 a30 30 0 1 0 60 0 a30 30 0 1 0 -60 0 Z' },
    { id: 'shell', label: "Nib's shell", hint: '#D8A05C', d: 'M330 344 m-44 0 a44 44 0 1 0 88 0 a44 44 0 1 0 -88 0 Z' },
    { id: 'snail-foot', label: 'Nib', hint: '#EFDFCB', d: 'M270 388 C270 368 300 358 342 358 C386 358 418 366 432 378 C440 386 436 394 414 394 L286 394 C274 394 270 392 270 388 Z' },
  ],
  outline: `
    M494 86 a54 54 0 1 0 108 0 a54 54 0 1 0 -108 0
    M94 320 a34 34 0 1 0 68 0 a34 34 0 1 0 -68 0
    M128 354 L128 400
    M526 336 a30 30 0 1 0 60 0 a30 30 0 1 0 -60 0
    M556 366 L556 404
    M270 388 C270 368 300 358 342 358 C386 358 418 366 432 378 C440 386 436 394 414 394 L286 394 C274 394 270 392 270 388 Z
    M286 344 a44 44 0 1 0 88 0 a44 44 0 1 0 -88 0
    M330 300 A44 44 0 1 1 300 372 A22 22 0 1 0 344 330
    M424 372 C424 348 428 332 438 318
    M408 374 C404 354 398 340 388 328
  `,
  backgroundOutline: `
    M0 232 C90 206 176 232 258 216 C346 198 408 226 490 210 C560 196 616 220 660 208
    M0 296 C110 276 210 300 320 288 C430 276 540 300 660 286
    M60 330 C56 312 58 300 64 288
    M622 344 C618 326 620 314 626 302
  `,
};

export const LIVING_SCENES: Record<string, LivingScene> = {
  /**
   * Nib glides across the leaf.
   *
   * Slow on purpose, and the only living scene that moves in a straight line
   * at a steady speed — the story is about something that is not in a hurry,
   * and an eased, swooping animation would contradict it.
   */
  scene_nib_glide: {
    id: 'scene_nib_glide',
    templateId: 'tpl_nib_shell',
    templateRevision: 1,
    durationMs: 7000,
    // The busy page, not the waiting one: that page has no snail in it, so the
    // child's snail is the only one on screen rather than gliding over a
    // drawn copy of itself. It also puts their slow animal into the page about
    // everything being fast, which is the story.
    backdropSceneId: 'nib-p1-busy',
    omitRegions: ['morning', 'leaf'],
    staticDescription:
      'Your snail rests on a wide green leaf in the morning garden, in the colours you chose.',
    steps: [
      { target: 'artwork', from: { x: -46, y: 24, scale: 0.64, opacity: 0 }, to: { x: -46, y: 24, scale: 0.64, opacity: 1 }, startMs: 0, endMs: 900, easing: 'ease-out' },
      { target: 'artwork', from: { x: -46, y: 24, scale: 0.64 }, to: { x: 18, y: 22, scale: 0.68 }, startMs: 900, endMs: 6100, easing: 'linear' },
      { target: 'artwork', from: { x: 18, y: 22, scale: 0.68, rotate: 0 }, to: { x: 22, y: 21, scale: 0.7, rotate: 1.5 }, startMs: 6100, endMs: 7000, easing: 'ease-out' },
    ],
  },
  scene_sway_flight: {
    id: 'scene_sway_flight',
    templateId: 'tpl_sway_wings',
    templateRevision: 2,
    durationMs: 6200,
    backdropSceneId: 'pip-p3-sway',
    omitRegions: ['sky'],
    staticDescription:
      'Your moth rests on a moonlit branch, wings open, with the colours you chose.',
    steps: [
      { target: 'artwork', from: { x: -26, y: 16, scale: 0.72, opacity: 0 }, to: { x: -26, y: 16, scale: 0.72, opacity: 1 }, startMs: 0, endMs: 700, easing: 'ease-out' },
      { target: 'artwork', from: { x: -26, y: 16, scale: 0.72, rotate: -6 }, to: { x: 14, y: -14, scale: 0.9, rotate: 5 }, startMs: 700, endMs: 3000, easing: 'ease-in-out' },
      { target: 'artwork', from: { x: 14, y: -14, scale: 0.9, rotate: 5 }, to: { x: 26, y: 6, scale: 0.82, rotate: -3 }, startMs: 3000, endMs: 4800, easing: 'ease-in-out' },
      { target: 'artwork', from: { x: 26, y: 6, scale: 0.82, rotate: -3 }, to: { x: 0, y: -4, scale: 0.95, rotate: 0 }, startMs: 4800, endMs: 6200, easing: 'ease-out' },
    ],
  },
  scene_puddle_sky: {
    id: 'scene_puddle_sky',
    templateId: 'tpl_morning_puddle',
    templateRevision: 1,
    durationMs: 6000,
    backdropSceneId: 'storm-p5-morning',
    omitRegions: ['above', 'grass'],
    staticDescription:
      'Your puddle sits in the wet grass with the morning sky caught inside it, in the colours you chose.',
    steps: [
      { target: 'artwork', from: { y: 24, scale: 0.9, opacity: 0 }, to: { y: 14, scale: 0.92, opacity: 1 }, startMs: 0, endMs: 800, easing: 'ease-out' },
      { target: 'artwork', from: { y: 14, scale: 0.92, rotate: -1 }, to: { y: 6, scale: 1.02, rotate: 1 }, startMs: 800, endMs: 3600, easing: 'ease-in-out' },
      { target: 'artwork', from: { y: 6, scale: 1.02, rotate: 1 }, to: { y: 10, scale: 0.98, rotate: -1 }, startMs: 3600, endMs: 6000, easing: 'ease-in-out' },
    ],
  },
  scene_moon_rise: {
    id: 'scene_moon_rise',
    templateId: 'tpl_paper_moon',
    templateRevision: 1,
    durationMs: 5600,
    backdropSceneId: 'pip-p6-paper-moon',
    omitRegions: ['night'],
    staticDescription:
      'Your paper moon hangs high over the pond, and the water below glows with its colour.',
    steps: [
      { target: 'artwork', from: { y: 46, scale: 0.86, opacity: 0 }, to: { y: 30, scale: 0.88, opacity: 1 }, startMs: 0, endMs: 900, easing: 'ease-out' },
      { target: 'artwork', from: { y: 30, scale: 0.88 }, to: { y: -18, scale: 1 }, startMs: 900, endMs: 4200, easing: 'ease-in-out' },
      { target: 'artwork', from: { y: -18, scale: 1, rotate: 0 }, to: { y: -22, scale: 1, rotate: 2 }, startMs: 4200, endMs: 5600, easing: 'ease-in-out' },
    ],
  },
};
